import { useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { connect } from 'react-redux';

import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import styles from './KnmSubjectItInfo.module.scss';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { IKnmSubject248ItAccreditationResponse } from '../../../APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { ItAccreditationResponseOrgStatusDictionary } from '../../../APIPackage/KnmModule/enums/ItAccreditationResponseOrgStatusEnum';
import { convertInitialKnmDateToFormDate } from '../common/convertInitialKnmDateToFormDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import {
  CHECKING_IN_REGESTRY_ORDER,
  CHECKING_IT_ACCREDITATION,
} from '../../../CorePackage/MainModule/constants/notices';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmDataCreated } from '../selectors/knmSelectors';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultFieldEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';

interface IKnmSubjectItInfoProps {
  checkByInnMessage: string | undefined;
  ervkId: string | undefined;
  inn?: string;
  isPresentInItRegistry: boolean | undefined;
  inspectionResults: IInspectionResult[] | undefined;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  isShowMessages?: boolean;
  name?: string;
  smevItAccreditationRequestStatus: string | undefined;
}

interface IKnmSubjectItInfoStoreProps {
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
}

const KnmSubjectItInfoWithoutStore = ({
  checkByInnMessage,
  ervkId,
  inn,
  isPresentInItRegistry,
  itAccreditationResponse,
  isShowMessages,
  name,
  smevItAccreditationRequestStatus,
  created,
  settings,
  inspectionResults,
}: IKnmSubjectItInfoProps & IKnmSubjectItInfoStoreProps) => {
  const {
    input: { value: reasonRequirements },
  } = useField('reasonRequirements');
  const {
    input: { value: orderRequirements },
  } = useField('orderRequirements');

  const [presentInItRegistryText, setPresentInItRegistryText] = useState<string>('');

  const isShowCheckResultMessage = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.SHOW_CHECK_RESULT_MESSAGE_523_4_1_17,
  );

  const isShowMessage5234153 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const IRItRegistry = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.IT_REGISTRY);

  /**
   * Ошибка валидации IT-организаций
   */
  const isErrorValidationItCompany = useMemo(() => {
    if (orderRequirements && isShowMessage5234153) {
      if (orderRequirements.flagIt !== null) {
        if (
          isPresentInItRegistry !== undefined &&
          ((orderRequirements.flagIt === 'true' && !isPresentInItRegistry) ||
            (orderRequirements.flagIt === 'false' && isPresentInItRegistry))
        ) {
          return CHECKING_IN_REGESTRY_ORDER;
        }
      }
    } else if (reasonRequirements?.it !== null) {
      if (
        isPresentInItRegistry !== undefined &&
        ((reasonRequirements?.it === true && !isPresentInItRegistry) ||
          (reasonRequirements?.it === false && isPresentInItRegistry))
      ) {
        return CHECKING_IT_ACCREDITATION;
      }
    }

    return '';
  }, [reasonRequirements]);

  const waitFromItWarning = () => {
    if (!isShowMessages || ervkId) return undefined;

    if (!isShowCheckResultMessage && !itAccreditationResponse && inn) {
      return 'Ожидайте загрузки информации из реестра аккредитованных ИТ-компаний, время ожидания составляет до 2 часов';
    }

    switch (smevItAccreditationRequestStatus) {
      case undefined:
      case '':
        return inn && !itAccreditationResponse
          ? 'Сохраните КНМ и ожидайте загрузки информации из реестра аккредитованных ИТ-компаний, время ожидания составляет до 2 часов'
          : undefined;
      case 'ERROR_RETRY':
      case 'REQUEST_SENT':
        return 'Ожидайте загрузки информации из реестра аккредитованных ИТ-компаний, время ожидания составляет до 2 часов';
      default:
        return undefined;
    }
  };

  const hasValueForIsPresentInItRegistryField =
    isPresentInItRegistry !== undefined &&
    (isPresentInItRegistry || itAccreditationResponse?.organizationStatus !== 'ABSENT');

  useEffect(() => {
    if (isPresentInItRegistry === undefined) {
      setPresentInItRegistryText('');
    } else {
      if (isPresentInItRegistry) {
        setPresentInItRegistryText('Да');
      }
      if (!isPresentInItRegistry) {
        setPresentInItRegistryText('Нет');
      }
    }
  }, [isPresentInItRegistry]);

  return (
    <>
      <Row>
        <FieldBlock
          label="Наличие в реестре аккредитованных IT-организаций"
          placementLabel="left"
          warning={waitFromItWarning() || checkByInnMessage}
          error={isErrorValidationItCompany}
        >
          <TextInputField
            id={`${name ? `${name}.` : ''}isPresentInItRegistry`}
            name={`${name ? `${name}.` : ''}isPresentInItRegistry`}
            className={`shared-row-field ${styles.HiddenTextField}`}
            disabled
          />
          <TextInput
            className="shared-row-field"
            value={presentInItRegistryText}
            inspectionResult={IRItRegistry}
            disabled
          />
        </FieldBlock>
      </Row>

      {itAccreditationResponse && hasValueForIsPresentInItRegistryField && (
        <div className={styles.itRow}>
          <FieldBlock label="Регистрационный номер" placementLabel="left" size="half" labelSize="col-3">
            <TextInputField
              className={`shared-row-field`}
              name={`${name ? `${name}.` : ''}.itAccreditationResponse.registryNumber`}
              disabled
            />
          </FieldBlock>
          <FieldBlock label="Дата включения" placementLabel="left" size="col-4">
            <TextInput
              className="shared-row-field"
              value={convertInitialKnmDateToFormDate(
                itAccreditationResponse?.dateDecisionAccreditation,
                DATE_FORMAT_LONG_FNS,
                SYSTEM_DATE_FORMAT_FNS,
              )}
              disabled
            />
          </FieldBlock>
          <FieldBlock label="Статус" placementLabel="left" size="col-4">
            <TextInput
              className={`shared-row-field`}
              value={
                ItAccreditationResponseOrgStatusDictionary[itAccreditationResponse?.organizationStatus || 'UNKNOWN']
              }
              disabled
            />
          </FieldBlock>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmSubjectItInfoStoreProps => ({
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
});

export const KnmSubjectItInfo = connect(mapStateToProps)(KnmSubjectItInfoWithoutStore);
